import type { Ref } from 'vue';
import type { SubmitEventPromise } from 'vuetify';

import { onMounted, ref, watch } from 'vue';

import { setDelayedAutofocus } from '@/utils/html';

export interface VFormInstance {
  validate(): Promise<{ valid: boolean; errors: unknown[] }>;
  reset(): void;
  resetValidation(): void;
  requestSubmit(): void;
}

export interface UseFormComposable {
  form: Ref<VFormInstance | null>;
  handleSubmit: (
    event?: SubmitEventPromise | null,
    cbParams?: unknown,
  ) => Promise<void>;
}

export interface Options {
  watcher?: boolean;
  resetOnSubmit?: boolean;
}

export default function useDefaultForm(
  props: { initialData: object; loading?: boolean },
  formData: Record<string, unknown>,
  emitCb: (event?: SubmitEventPromise | null, cbParams?: unknown) => void,
  options: Options = {},
): UseFormComposable {
  const form = ref<VFormInstance | null>(null);
  if (options.watcher === undefined) options.watcher = true;

  if (options.watcher) {
    watch(
      () => props.initialData,
      (initialData) => {
        // note that old values of fromData are not cleared by this
        Object.keys(initialData).forEach((key) => {
          formData[key] = (initialData as Record<string, unknown>)[key];
        });
      },
      { immediate: true, deep: true },
    );
  }

  async function handleSubmit(
    event?: SubmitEventPromise | null,
    cbParams?: unknown,
  ): Promise<void> {
    if (form.value === null) return;
    const { valid } = await form.value.validate();
    if (!valid) return;
    emitCb(event, cbParams);
    if (options.resetOnSubmit) {
      const result = props.loading;
      if (result === false) {
        form.value.reset();
      }
    }
  }

  onMounted(function () {
    setDelayedAutofocus(form.value as unknown as Element | null);
  });

  return { form, handleSubmit };
}
